export interface RequestAbortHandle {
  signal: AbortSignal;
  didTimeout: () => boolean;
  cleanup: () => void;
}

export function createRequestAbortHandle(
  upstream?: AbortSignal | null,
  timeoutMs = 12_000,
): RequestAbortHandle {
  const controller = new AbortController();
  let timedOut = false;
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;

  const clearDeadline = () => {
    if (timer !== undefined) globalThis.clearTimeout(timer);
    timer = undefined;
  };

  const abortFromUpstream = () => {
    clearDeadline();
    if (!controller.signal.aborted) controller.abort(upstream?.reason);
  };

  if (upstream?.aborted) abortFromUpstream();
  else upstream?.addEventListener('abort', abortFromUpstream, { once: true });

  // An already-aborted upstream request is terminal. Do not retain a timeout
  // that can later relabel that cancellation as a timeout or wake the event loop
  // after the caller has no remaining request work.
  if (!controller.signal.aborted) {
    timer = globalThis.setTimeout(() => {
      timedOut = true;
      if (!controller.signal.aborted) controller.abort();
    }, Math.max(1, timeoutMs));
  }

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    cleanup: () => {
      clearDeadline();
      upstream?.removeEventListener('abort', abortFromUpstream);
    },
  };
}
